import type { Coordinate } from '../models/editor-feature';

/**
 * Whether an outline is drawn as a desk: the "[" every desk on these plans is drawn as.
 *
 * The same rule as the server's `DeskOutlineShape`, which is what the desk tagger now marks by.
 * A worktop along one side with an arm along each end, open where the chair goes — eight
 * corners, six turning one way and the two inside corners of the opening turning the other, all
 * close to square. Rotation, size, mirroring, winding, a closing repeat and extra points along a
 * straight side do not matter.
 */

/** Corners of the "[": the outer four, the two ends of the opening, and its two inside corners. */
const CORNERS = 8;

/** A turn smaller than this, in degrees, is a point along a straight side, not a corner. */
const STRAIGHT_TURN_DEGREES = 20;

/** How far, in degrees, a corner may be from square and still be one. */
const SQUARE_TOLERANCE_DEGREES = 30;

/** Points closer together than this share of the outline's size are one point. */
const SAME_POINT_FRACTION = 0.0005;

const distance = (first: Coordinate, second: Coordinate): number => Math.hypot(first[0] - second[0], first[1] - second[1]);

/** The signed turn at each corner, in degrees: positive to the left. */
const turnsOf = (corners: readonly Coordinate[]): number[] => corners.map((current, index) => {
  const previous = corners[(index - 1 + corners.length) % corners.length];
  const next = corners[(index + 1) % corners.length];
  const inX = current[0] - previous[0];
  const inY = current[1] - previous[1];
  const outX = next[0] - current[0];
  const outY = next[1] - current[1];

  return (Math.atan2((inX * outY) - (inY * outX), (inX * outX) + (inY * outY)) * 180) / Math.PI;
});

/** The outline's corners: no closing repeat, no repeated points, no points along a straight side. */
const cornersOf = (ring: readonly Coordinate[]): Coordinate[] => {
  const points = ring.filter((point) => point.length >= 2).map(([x, y]): Coordinate => [x, y]);

  if (points.length < 3) {
    return [];
  }

  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const size = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));

  if (!(size > 0)) {
    return [];
  }

  const samePoint = size * SAME_POINT_FRACTION;
  const distinct: Coordinate[] = [];

  points.forEach((point) => {
    if (!distinct.length || distance(distinct[distinct.length - 1], point) > samePoint) {
      distinct.push(point);
    }
  });

  while (distinct.length > 1 && distance(distinct[0], distinct[distinct.length - 1]) <= samePoint) {
    distinct.pop();
  }

  // One point at a time, the straightest first, so a side drawn in many short pieces collapses
  // to its two ends without a gentle bend elsewhere being taken with it.
  while (distinct.length > 3) {
    const turns = turnsOf(distinct);
    let straightest = 0;

    turns.forEach((turn, index) => {
      if (Math.abs(turn) < Math.abs(turns[straightest])) straightest = index;
    });

    if (Math.abs(turns[straightest]) >= STRAIGHT_TURN_DEGREES) {
      break;
    }

    distinct.splice(straightest, 1);
  }

  return distinct;
};

export const isDeskOutline = (ring: readonly Coordinate[]): boolean => {
  const corners = cornersOf(ring);

  if (corners.length !== CORNERS) {
    return false;
  }

  const turns = turnsOf(corners);
  const orientation = Math.sign(turns.reduce((total, turn) => total + turn, 0));

  if (orientation === 0 || turns.some((turn) => Math.abs(Math.abs(turn) - 90) > SQUARE_TOLERANCE_DEGREES)) {
    return false;
  }

  // Exactly two corners turning against the outline — the inside of the opening — and next to
  // each other. Two apart would be a "Z" or an "S"; one alone is an "L".
  const inward = turns.flatMap((turn, index) => (Math.sign(turn) !== orientation ? [index] : []));

  return inward.length === 2
    && (inward[1] - inward[0] === 1 || (inward[0] === 0 && inward[1] === CORNERS - 1));
};

export default isDeskOutline;
